import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { 
  Syringe,
  Activity,
  Stethoscope,
  HeartPulse,
  Eye,
  Bone,
  Baby,
  Pill,
  ChevronRight,
  Check
} from "lucide-react";

const categories = [
  { id: "anesthesia", label: "Anesthesia Types", icon: Syringe },
  { id: "procedures", label: "Surgical Support", icon: Stethoscope },
  { id: "pain", label: "Pain Management", icon: HeartPulse },
  { id: "specialty", label: "Specialty Care", icon: Activity },
];

const services = {
  anesthesia: [
    {
      icon: Syringe,
      name: "General Anesthesia",
      shortDesc: "Complete sedation for surgical procedures",
      fullDesc: "General anesthesia provides complete unconsciousness during surgery. Our board-certified anesthesiologists carefully monitor all vital signs throughout your procedure, ensuring your safety and comfort.",
      features: ["Pre-operative evaluation", "Continuous vital sign monitoring", "Airway management", "Post-operative care"],
    },
    {
      icon: Activity,
      name: "IV Sedation / MAC",
      shortDesc: "Monitored anesthesia care for minor procedures",
      fullDesc: "Monitored Anesthesia Care (MAC) provides sedation while you remain breathing on your own. Ideal for shorter procedures, colonoscopies, and minimally invasive surgeries.",
      features: ["Twilight sedation", "Rapid recovery", "Anxiety reduction", "Continuous monitoring"],
    },
    {
      icon: Stethoscope,
      name: "Regional Anesthesia",
      shortDesc: "Targeted nerve blocks for specific areas",
      fullDesc: "Regional anesthesia numbs a specific area of your body without affecting your consciousness. Options include spinal, epidural, and peripheral nerve blocks.",
      features: ["Spinal anesthesia", "Epidural blocks", "Nerve blocks", "Reduced systemic effects"],
    },
  ],
  procedures: [
    {
      icon: Eye,
      name: "Ophthalmology Support",
      shortDesc: "Anesthesia for eye surgeries",
      fullDesc: "Specialized anesthesia care for cataract surgery, LASIK, retinal procedures, and other ophthalmic surgeries. We ensure patient comfort and immobility during delicate procedures.",
      features: ["Cataract surgery", "Retinal procedures", "Glaucoma surgery", "Oculoplastic surgery"],
    },
    {
      icon: Bone,
      name: "Orthopedic Anesthesia",
      shortDesc: "Support for bone and joint procedures",
      fullDesc: "Comprehensive anesthesia services for orthopedic surgeries including joint replacements, arthroscopy, fracture repairs, and sports medicine procedures.",
      features: ["Joint replacement", "Arthroscopic surgery", "Fracture repair", "Spine procedures"],
    },
    {
      icon: Stethoscope,
      name: "Plastic Surgery Support",
      shortDesc: "Anesthesia for cosmetic procedures",
      fullDesc: "Expert anesthesia care for cosmetic and reconstructive procedures. We work closely with plastic surgeons to ensure optimal patient outcomes.",
      features: ["Facial procedures", "Body contouring", "Breast surgery", "Reconstructive surgery"],
    },
  ],
  pain: [
    {
      icon: HeartPulse,
      name: "Post-Operative Pain Control",
      shortDesc: "Managing pain after surgery",
      fullDesc: "Comprehensive post-operative pain management strategies to ensure comfortable recovery. We utilize multimodal approaches to minimize opioid requirements.",
      features: ["IV pain medications", "Regional blocks", "Oral pain management", "Recovery protocols"],
    },
    {
      icon: Activity,
      name: "Nerve Blocks",
      shortDesc: "Targeted pain relief injections",
      fullDesc: "Peripheral nerve blocks provide targeted pain relief for specific surgical sites. These can significantly reduce post-operative pain and speed recovery.",
      features: ["Upper extremity blocks", "Lower extremity blocks", "Trunk blocks", "Extended relief"],
    },
    {
      icon: Pill,
      name: "Multi-Modal Analgesia",
      shortDesc: "Combined approach pain management",
      fullDesc: "Our multi-modal approach combines different pain management techniques to provide superior pain relief while minimizing side effects and opioid use.",
      features: ["Non-opioid medications", "Local anesthetics", "Anti-inflammatory drugs", "Personalized plans"],
    },
  ],
  specialty: [
    {
      icon: Baby,
      name: "Pediatric Anesthesia",
      shortDesc: "Specialized care for children",
      fullDesc: "Our pediatric-trained anesthesiologists provide age-appropriate anesthesia care for children, with special attention to their unique physiological needs and comfort.",
      features: ["Child-friendly approach", "Age-appropriate dosing", "Family support", "Specialized monitoring"],
    },
    {
      icon: HeartPulse,
      name: "Cardiac Risk Patients",
      shortDesc: "Care for high-risk patients",
      fullDesc: "Specialized anesthesia protocols for patients with cardiac conditions. Our team has extensive experience managing patients with heart disease safely.",
      features: ["Pre-op cardiac evaluation", "Advanced monitoring", "Risk stratification", "Cardiac protocols"],
    },
    {
      icon: Activity,
      name: "GI Endoscopy",
      shortDesc: "Sedation for diagnostic procedures",
      fullDesc: "Expert sedation services for colonoscopy, upper endoscopy, and other gastrointestinal procedures. We ensure patient comfort while maintaining safety.",
      features: ["Colonoscopy sedation", "Upper endoscopy", "ERCP support", "Quick recovery"],
    },
  ],
};

export default function Services() {
  const [activeTab, setActiveTab] = useState("anesthesia");

  return (
    <div data-testid="services-page" className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-vip-black relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 70% 30%, rgba(255, 0, 255, 0.1) 0%, transparent 50%)'
          }}
        />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-neon-pink uppercase tracking-[0.2em] text-sm mb-4">Our Services</p>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-none font-bold italic text-white mb-8">
            Anesthesia <span className="gradient-text">Services</span>
          </h1>
          <p className="text-gray-300 text-base md:text-lg lg:text-xl max-w-2xl mx-auto">
            Comprehensive anesthesia care for outpatient surgical procedures. 
            Board-certified anesthesiologists providing safe, comfortable experiences.
          </p>
        </div>
      </section>

      {/* Services Tabs */}
      <section data-testid="services-tabs" className="py-24 md:py-32 bg-vip-paper">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {/* Tab List */}
            <TabsList className="flex flex-wrap justify-center gap-3 md:gap-4 bg-transparent mb-16">
              {categories.map((cat) => (
                <TabsTrigger
                  key={cat.id}
                  value={cat.id}
                  data-testid={`tab-${cat.id}`}
                  className={`flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 rounded-full border transition-all duration-300 text-xs md:text-sm ${
                    activeTab === cat.id
                      ? "bg-white text-black border-white shadow-[0_0_20px_rgba(255,255,255,0.3)]"
                      : "bg-transparent text-gray-300 border-white/20 hover:border-white/40"
                  }`}
                >
                  <cat.icon className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="font-body uppercase tracking-wider hidden sm:inline">{cat.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Tab Content */}
            {categories.map((cat) => (
              <TabsContent key={cat.id} value={cat.id} className="mt-0">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {services[cat.id].map((service, index) => (
                    <Dialog key={service.name}>
                      <DialogTrigger asChild>
                        <div
                          data-testid={`service-${cat.id}-${index}`}
                          className="vip-card glass rounded-2xl p-8 border border-white/10 hover:border-neon-cyan/30 cursor-pointer group"
                        >
                          <div className="service-icon w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:neon-cyan-glow transition-all duration-300">
                            <service.icon className="w-8 h-8 text-neon-cyan" />
                          </div>
                          <h3 className="font-heading text-xl text-white mb-3">{service.name}</h3>
                          <p className="text-gray-400 text-sm mb-6">{service.shortDesc}</p>
                          <span className="text-neon-cyan text-sm uppercase tracking-wider flex items-center gap-2 group-hover:gap-3 transition-all duration-300">
                            Learn More <ChevronRight className="w-4 h-4" />
                          </span>
                        </div>
                      </DialogTrigger>
                      <DialogContent className="bg-vip-paper border border-white/10 text-white max-w-lg">
                        <DialogHeader>
                          <div className="flex items-center gap-4 mb-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-neon-cyan/20 to-neon-pink/20 flex items-center justify-center">
                              <service.icon className="w-6 h-6 text-neon-cyan" />
                            </div>
                            <DialogTitle className="font-heading text-2xl">{service.name}</DialogTitle>
                          </div>
                          <DialogDescription className="text-gray-300 text-base leading-relaxed">
                            {service.fullDesc}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="mt-6">
                          <h4 className="text-sm uppercase tracking-wider text-neon-pink mb-4">Includes</h4>
                          <ul className="space-y-3">
                            {service.features.map((feature, idx) => (
                              <li key={idx} className="flex items-center gap-3 text-gray-300">
                                <Check className="w-4 h-4 text-neon-cyan" />
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="mt-8">
                          <Link to="/appointments">
                            <Button
                              data-testid="service-book-btn"
                              className="w-full rounded-full bg-white text-black hover:bg-neon-cyan hover:text-black font-bold uppercase tracking-widest py-4"
                            >
                              Schedule Consultation
                            </Button>
                          </Link>
                        </div>
                      </DialogContent>
                    </Dialog>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Process Section */}
      <section data-testid="process-section" className="py-24 md:py-32 bg-vip-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-neon-cyan uppercase tracking-[0.2em] text-sm mb-4">Your Journey</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium text-white">
              What to Expect
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Pre-Op Evaluation", desc: "Medical history review and physical assessment" },
              { step: "02", title: "Anesthesia Plan", desc: "Customized plan based on your needs" },
              { step: "03", title: "Day of Surgery", desc: "Continuous care and monitoring" },
              { step: "04", title: "Recovery", desc: "Safe discharge and follow-up care" },
            ].map((item, index) => (
              <div key={index} data-testid={`process-step-${index}`} className="text-center relative">
                <div className="text-5xl md:text-6xl font-heading font-bold gradient-text mb-4 opacity-50">
                  {item.step}
                </div>
                <h3 className="font-heading text-lg md:text-xl text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-neon-cyan/50 to-transparent -translate-x-1/2" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section className="py-24 md:py-32 bg-vip-paper">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-neon-pink uppercase tracking-[0.2em] text-sm mb-4">Patient Safety</p>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium text-white mb-8">
                Your Safety Is Our
                <br />
                <span className="gradient-text">Top Priority</span>
              </h2>
              <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6">
                At Surgery Center LLC, we maintain the highest standards of patient safety. 
                Our facility is fully accredited and equipped with the latest monitoring technology.
              </p>
              <ul className="space-y-4 text-gray-400">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-neon-cyan" />
                  Board-certified anesthesiologists on every case
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-neon-cyan" />
                  Advanced monitoring equipment
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-neon-cyan" />
                  AAAHC accredited facility
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-neon-cyan" />
                  Emergency protocols and equipment
                </li>
              </ul>
            </div>
            <div className="aspect-video rounded-2xl overflow-hidden border border-white/10">
              <img
                src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800&h=450&fit=crop"
                alt="Medical Monitoring Equipment"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-vip-black relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(0, 240, 255, 0.1) 0%, transparent 60%)'
          }}
        />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-bold italic text-white mb-6">
            Questions About <span className="gradient-text">Anesthesia</span>?
          </h2>
          <p className="text-gray-300 text-base md:text-lg mb-10">
            Schedule a consultation with our anesthesia team to discuss your upcoming procedure.
          </p>
          <Link to="/appointments">
            <Button
              data-testid="services-cta-btn"
              className="shimmer-btn rounded-full bg-white text-black hover:bg-neon-cyan hover:text-black font-bold uppercase tracking-widest px-10 py-6 text-base shadow-[0_0_30px_rgba(255,255,255,0.4)] transition-all duration-300"
            >
              Schedule Consultation
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
